import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, Select, DatePicker, Button } from 'antd';
import styled from 'styled-components';
import { Box } from 'atoms';

const { Option } = Select;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
	}

	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 0;
	}

	.ant-modal-title {
		font-size: 20px;
		font-weight: 600;
		color: #1f2937;
	}

	.ant-modal-body {
		padding: 24px;
	}

	.ant-modal-footer {
		border-top: none;
		padding: 0 24px 24px;
	}
`;

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #374151;
		font-size: 14px;
	}

	.ant-form-item-label > label::after {
		content: '*';
		color: #ef4444;
		margin-left: 4px;
	}

	.ant-input,
	.ant-select-selector,
	.ant-picker {
		height: 40px;
		border-radius: 6px;
		border: 1px solid #d1d5db;
		background-color: #f9fafb;
	}

	.ant-input:disabled,
	.ant-select-disabled .ant-select-selector {
		background-color: #e5e7eb;
		color: #6b7280;
	}

	.ant-select-selector {
		display: flex;
		align-items: center;
	}

	.ant-picker {
		width: 100%;
		display: flex;
		align-items: center;
	}

	.ant-input:hover,
	.ant-select-selector:hover,
	.ant-picker:hover {
		border-color: #9ca3af;
	}

	.ant-input:focus,
	.ant-select-focused .ant-select-selector,
	.ant-picker-focused {
		border-color: #1e3a8a;
		box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
	}
`;

const FormGrid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
`;

const ModalFooter = styled(Box)`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #ef4444;
	color: #ef4444;
	background: white;
	font-weight: 500;
	border-radius: 6px;

	&:hover {
		border-color: #dc2626 !important;
		color: #dc2626 !important;
		background: white !important;
	}
`;

const ConfirmButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	color: white;
	font-weight: 500;
	border-radius: 6px;

	&:hover {
		background-color: #1e40af !important;
		border-color: #1e40af !important;
		color: white !important;
	}

	&:disabled {
		background-color: #9ca3af !important;
		border-color: #9ca3af !important;
		cursor: not-allowed;
	}
`;

function TransferVehicleModal({ visible, vehicleData, vendorOptions, onConfirm, onClose }) {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (visible && vehicleData) {
			form.setFieldsValue({
				vehicle_number: vehicleData.vehicle_number,
				current_vendor: vehicleData.vendor_name,
				transfer_to_vendor: undefined,
				reason: '',
				last_date: undefined,
				start_date: undefined,
			});
		}
	}, [visible, vehicleData, form]);

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	const handleConfirm = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			const formatDate = (d) => `${d.format('YYYY-MM-DD')}T00:00:00Z`;

			const transferData = {
				vehicle_id: vehicleData.id,
				to_vendor_id: values.transfer_to_vendor,
				reason: values.reason,
				last_working_date: formatDate(values.last_date),
				start_date: formatDate(values.start_date),
			};

			const success = await onConfirm(transferData);
			if (success) {
				form.resetFields();
			}
		} catch (error) {
			console.error('Validation error:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<StyledModal
			title="Transfer Vehicle"
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={800}
			destroyOnClose
		>
			<StyledForm form={form} layout="vertical">
				<FormGrid>
					<Form.Item name="vehicle_number" label="Vehicle number">
						<Input disabled placeholder="Vehicle number" />
					</Form.Item>

					<Form.Item name="current_vendor" label="Current Vendor">
						<Input disabled placeholder="Current Vendor" />
					</Form.Item>

					<Form.Item
						name="transfer_to_vendor"
						label="Transfer to Vendor"
						rules={[{ required: true, message: 'Please select vendor' }]}
					>
						<Select placeholder="Select vendor" showSearch optionFilterProp="children">
							{vendorOptions.map((vendor) => (
								<Option key={vendor.value} value={vendor.value}>
									{vendor.label}
								</Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item
						name="reason"
						label="Reason for Transfer"
						rules={[{ required: true, message: 'Please enter reason' }]}
					>
						<Input placeholder="Enter reason" />
					</Form.Item>

					<Form.Item
						name="last_date"
						label="Last Date of working with Previous Vendor"
						rules={[{ required: true, message: 'Please select date' }]}
					>
						<DatePicker
							format="DD-MM-YYYY"
							placeholder="DD-MM-YYYY"
							style={{ width: '100%' }}
						/>
					</Form.Item>

					<Form.Item
						name="start_date"
						label="Start Date of working with New Vendor"
						rules={[{ required: true, message: 'Please select date' }]}
					>
						<DatePicker
							format="DD-MM-YYYY"
							placeholder="DD-MM-YYYY"
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</FormGrid>

				<ModalFooter>
					<CancelButton onClick={handleCancel}>Cancel</CancelButton>
					<ConfirmButton onClick={handleConfirm} loading={loading}>
						Confirm
					</ConfirmButton>
				</ModalFooter>
			</StyledForm>
		</StyledModal>
	);
}

TransferVehicleModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	vehicleData: PropTypes.shape({
		id: PropTypes.number,
		vehicle_number: PropTypes.string,
		vendor_id: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
		vendor_name: PropTypes.string,
	}),
	vendorOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.number,
			label: PropTypes.string,
		})
	).isRequired,
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default TransferVehicleModal;
